/**
 * Chart.js plugin that highlights the data point with the maximum value
 * by drawing a dashed vertical line and a larger colored circle at the peak.
 *
 * Configuration (via chart.options.plugins.peakMarker):
 *   color  – stroke / fill colour (default '#dc3545')
 *   radius – marker radius in px       (default 6)
 */
export function createPeakPlugin(opts = {}) {
    const defaultColor = opts.color || '#dc3545';
    const defaultRadius = opts.radius || 6;

    return {
        id: 'peakMarker',

        afterDatasetsDraw(chart) {
            const { ctx, data, scales, options } = chart;
            const cfg = options.plugins?.peakMarker || {};

            // Which dataset to use — default 0, configurable via cfg.datasetIndex
            const dsi = typeof cfg.datasetIndex === 'number' ? cfg.datasetIndex : 0;
            const dataset = data.datasets[dsi];
            if (!dataset || !dataset.data || dataset.data.length === 0) return;

            // Read per-chart config or fall back to defaults
            const color = cfg.color ?? defaultColor;
            const radius = cfg.radius ?? defaultRadius;

            // Find peak index
            let maxVal = -Infinity;
            let peakIndex = 0;
            dataset.data.forEach((val, i) => {
                if (val > maxVal) {
                    maxVal = val;
                    peakIndex = i;
                }
            });

            const meta = chart.getDatasetMeta(dsi);
            if (!meta || !meta.data[peakIndex]) return;

            const { x: peakX, y: peakY } = meta.data[peakIndex].getCenterPoint();
            const topY = scales.y.top;
            const bottomY = scales.y.bottom;

            // Dashed vertical line
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([6, 4]);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = color + '88';
            ctx.moveTo(peakX, topY);
            ctx.lineTo(peakX, bottomY);
            ctx.stroke();

            // Outer glow circle
            ctx.beginPath();
            ctx.setLineDash([]);
            ctx.arc(peakX, peakY, radius + 3, 0, Math.PI * 2);
            ctx.strokeStyle = color + '44';
            ctx.lineWidth = 2;
            ctx.stroke();

            // Inner circle
            ctx.beginPath();
            ctx.arc(peakX, peakY, radius, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();

            // White inner dot
            ctx.beginPath();
            ctx.arc(peakX, peakY, 2.5, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.restore();
        }
    };
}
